<template>
  <section
    class="bottom-header relative c-bg-black flex"
    :class="[
      product || artist ? (product ? 'product-page' : 'artist-page') : 'bottom-xs']"
  >
    <div
      v-if="artist"
      class="visible-mobile hidden-tablet hidden-desktop back-button absolute pointer c-white"
      @click="handleBack"
    >
      <i class="icon icon-arrow-back" />
    </div>
    <img
      v-if="!deviceIsMobile || !mobileImage"
      :src="image ? getImagePath(image) : '/assets/header_placeholder.jpg'"
      :alt="this.$t('Event Banner Image')"
      class="h-100 c-bg-black absolute main-image top-0 w-100"
      fetchpriority="high"
      :class="{ 'hidden-mobile' : mobileImage }"
    >
    <img
      v-if="mobileImage && deviceIsMobile"
      :src="getMobileImagePath(mobileImage)"
      class="main-image w-100 h-100 top-0 c-bg-black absolute visible-mobile hidden-tablet"
      fetchpriority="high"
      :alt="this.$t('Event Banner Image')"
    >

    <product-banner
      v-if="product"
      :product="product"
      :attributes="attributes"
    />

    <artist-banner
      v-else-if="artist"
      :artist="artist"
    />

    <div
      v-else
      class="container relative flex-column h-100 mb25-desktop mb25-tablet header-content"
    >
      <div class="content-wrapper row m0 w-100 px25 align-center justify-center c-white">
        <h1
          v-if="categoryName"
          class="col-xs-8 page-title c-white"
        >
          {{ categoryName }}
        </h1>
        <component
          :is="subheadingTag"
          class="col-xs-8 page-title concert-title fs28 mb18 c-white"
          :class="{'visible-xs': hideHeadingDesktop}"
          v-html="heading"
        />
        <div
          v-if="showSearch"
          class="search-wrapper w-100 px50-desktop"
        >
          <search-block
            :search-placeholder="$t('Search event or performer')"
          />
          <div class="w-100 fs14 c-white m0 mt7 regular mb15">
            <div
              v-if="pageNotFound"
              class="description"
            >
              {{ $t('Please use the search function above to find your next event.') }}
            </div>
            <div
              v-if="homePage"
              class="description"
            >
              {{ $t('The tickets on this website is sold on the secondary market. The price can be above the face value.') }}
            </div>
          </div>
        </div>
        <div
          class="w-100 description"
          :class="{descriptionClass}"
          v-html="description"
        />
      </div>
      <popular-artists
        v-if="categoryArtists"
        class="px25"
        :category-artists="categoryArtists"
        :show-description="false"
      />
    </div>

    <div
      v-if="getImageReference"
      class="image-reference"
    >
      <tooltip
        class="image-reference-tooltip"
        :text="getImageReference"
        :translate="false"
        :is-html="true"
      >
        <template #icon="{ visible, toggle }">
          <span
            class="image-reference-trigger pointer"
            @click.stop.prevent="toggle"
          >
            <i
              class="icon"
              :class="[ visible ? 'icon-info-new-active' : 'icon-info-new' ]"
            />
            <span class="image-reference-label">{{ $t('License') }}</span>
          </span>
        </template>
      </tooltip>
    </div>
  </section>
</template>

<script>
import ProductBanner from 'theme/components/theme/blocks/Product/ProductBanner.vue'
import ArtistBanner from 'theme/components/theme/blocks/Category/ArtistBanner'
import SearchBlock from 'theme/components/theme/blocks/Search/SearchBlock'
import { mapState } from 'vuex'
import { getMediaThumbnailPath, cleanPath } from 'theme/helpers'
import PopularArtists from 'src/modules/popular/component/PopularArtists'
import { htmlDecode } from '@vue-storefront/core/filters'
import Tooltip from 'theme/components/common/Tooltip'

export default {
  name: 'SiteHeader',
  components: {
    ArtistBanner,
    ProductBanner,
    SearchBlock,
    PopularArtists,
    Tooltip
  },
  props: {
    heading: {
      type: String,
      default: ''
    },
    hideHeadingDesktop: {
      type: Boolean,
      default: false
    },
    description: {
      type: String,
      default: ''
    },
    image: {
      type: String,
      default: ''
    },
    imageReference: {
      type: String,
      default: ''
    },
    mobileImage: {
      type: String,
      default: '',
      required: false
    },
    descriptionClass: {
      type: String,
      default: ''
    },
    product: {
      type: Object,
      default: () => { }
    },
    artist: {
      type: Object,
      default: () => { }
    },
    attributes: {
      type: Array,
      default: () => { }
    },
    showSearch: {
      type: Boolean,
      default: true
    },
    categoryName: {
      type: String,
      default: ''
    },
    pageNotFound: {
      type: Boolean,
      default: false
    },
    homePage: {
      type: Boolean,
      default: false
    },
    categoryArtists: {
      type: Array,
      default: () => { }
    }
  },
  data () {
    return {
      onlyMobile: true,
      deviceIsMobile: true
    }
  },
  computed: {
    ...mapState({
      searchSuggestions: state => state.ui.searchSuggestions,
      prevRouteState: state => state.url.prevRoute.path
    }),
    getImageReference () {
      if (this.imageReference) {
        return htmlDecode(this.imageReference)
      }

      if (this.artist && this.artist.image_reference) {
        return htmlDecode(this.artist.image_reference)
      }

      return ''
    },
    subheadingTag () {
      return this.categoryName ? 'h2' : 'h1'
    }
  },
  mounted () {
    this.updateIsMobile()
    window.addEventListener('resize', this.updateIsMobile)

    this.onlyMobileCheck()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.updateIsMobile)
  },
  methods: {
    handleBack () {
      const path = this.prevRouteState || ''
      const isInternalPath = path.startsWith('/')

      const isEventUrl = /-\d{4}-\d{2}-\d{2}-\d{1,2}-\d{2}/.test(path)

      if (!isInternalPath || isEventUrl || path === this.$route.path) {
        this.$router.replace('/')

        return
      }

      this.$router.back()
    },
    onlyMobileCheck () {
      this.onlyMobile = !!this.categoryName
    },
    updateIsMobile () {
      this.deviceIsMobile = window.innerWidth <= 767
    },
    getImagePath (image) {
      if (this.homePage || this.pageNotFound || this.product) {
        return image
      }
      return getMediaThumbnailPath(cleanPath(image), 1920, 1080)
    },
    getMobileImagePath (image) {
      if (this.homePage || this.pageNotFound || this.product) {
        return image
      }

      return getMediaThumbnailPath(cleanPath(image), 750, 450)
    }
  }
}
</script>
<style lang="scss" scoped>
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';
@import '~theme/css/variables/grid';
@import '~theme/css/helpers/functions/z-index-selectors';

.bottom-header {
  z-index: zIndex(header);

  .image-reference {
    position: absolute;
    right: 5px;
    bottom: 5px;
    z-index: zIndex(banner);
    text-align: right;
    font-size: 11px;
    line-height: 14px;
    color: #d0d0d0;

    .image-reference-trigger {
      display: inline-flex;
      align-items: center;
      gap: 3px;
    }

    /deep/ a {
      color: #d0d0d0;
      text-decoration: underline;
      font-size: 11px;
      line-height: 14px;
    }

    /deep/ p {
      margin: 0;
      padding: 0;
      display: inline;
    }
  }

  .back-button {
    top: 20px;
    left: 20px;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.25);
    z-index: 10;

    .icon {
      width: 24px;
      height: 24px;
    }
  }

  .header-content {
    padding: 0;
  }

  .concert-title {
    color: #ddd;
    font-weight: 400;
    margin-top: 0.67em;
    margin-bottom: 0.67em;
  }

  &.product-page {
    min-height: 280px;
    max-height: 280px;
  }

  &.artist-page {
    min-height: 220px;
    max-height: 220px;
    flex-direction: row-reverse;
  }

  .main-image {
    object-position: center;
    object-fit: cover;
    opacity: 0.6;
    inset: 0;
  }

  &::after {
    background: linear-gradient(rgba(color(black), 0), color(black));
    bottom: 0;
    top: inherit;
  }

  .content-wrapper {
    z-index: zIndex(banner);
  }

  .description {
    color: #ddd;
  }
}

@include mediaQuery(sm) {
  .bottom-header {
    &.artist-page,
    &.product-page {
      padding-bottom: 0;
    }
  }
}

@include mediaQuery(null, md) {
  .bottom-header {
    .back-button {
      display: flex;
      align-items: center;
      justify-content: center;
      touch-action: manipulation;
      -webkit-tap-highlight-color: transparent;
    }

    .content-wrapper {
      justify-content: flex-start;
      text-align: left;
    }

    .page-title:not(.concert-title) {
      font-size: 28px;
      line-height: 32px;
      text-align: left;
      margin-bottom: 0;
    }

    .concert-title {
      font-size: 18px;
      line-height: 22px;
      text-align: left;
      margin-top: 0;
    }

    .description {
      margin-bottom: 30px;
    }
  }
}

@include tablet {
  .bottom-header {
    padding-left: 85px;
    padding-right: 85px;
  }
}

@include desktop {
  .bottom-header {
    min-height: 410px;
    padding-left: 85px;
    padding-right: 85px;

    &.product-page {
      min-height: 496px;
    }

    .page-title {
      margin-bottom: 50px;
    }

    .concert-title {
      font-size: 26px;
      line-height: 29px;
    }

    .description {
      margin-bottom: 0;
      font-size: 16px;
    }

    &.artist-page,
    &.product-page {
      max-height: 280px;
      min-height: 280px;
      padding-top: 0;
      border-radius: 10px;
      overflow: visible;

      .main-image {
        max-width: 420px;
        left: 0;
        opacity: 0.8;
        border-radius: 10px;
      }

      &::before {
        all: unset;
      }

      &::after {
        content: "";
        height: 100%;
        width: 150px;
        background: linear-gradient(to right, rgba(color(black), 0), color(black));
        position: absolute;
        left: 270px;
        border-radius: 10px;
      }
    }
  }

  .site-header {
    border-radius: 10px;

    .main-image {
      border-radius: 10px;
    }

    padding-left: 50px;
    padding-right: 50px;

    .page-title {
      margin-bottom: 0;
    }

    .concert-title {
      font-size: 20px;
      line-height: 22px;
      margin-bottom: 20px;
    }

    .description {
      margin-bottom: 40px;
    }

    h1 {
      font-size: 48px;
    }
  }
}

@media (min-width: 1025px) and (max-width: 1400px) {
  .search-wrapper {
    margin: 0;
    padding-left: 0;
    padding-right: 0;
  }
}
</style>
